import React, { useState, useRef, useEffect } from "react";
import "./style.css";

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 5;
const ZOOM_SENSITIVITY = 0.008;

const Map = ({ src, zoom, setZoom, rightPaneOpen, dmMapUrl }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [initialDistance, setInitialDistance] = useState(null);
  const containerRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const initialPositionRef = useRef({ x: 0, y: 0 });
  const imgRef = useRef(null);

  // Calculate distance between two touch points
  const calculateDistance = (touches) => {
    return Math.hypot(
      touches[0].clientX - touches[1].clientX,
      touches[0].clientY - touches[1].clientY
    );
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const zoomSpeed = 0.1;
    const scaleAmount = zoom * zoomSpeed;
    const newZoom =
      e.deltaY > 0
        ? Math.max(MIN_ZOOM, zoom - scaleAmount)
        : Math.min(MAX_ZOOM, zoom + scaleAmount);
    setZoom(newZoom);
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
    };
    initialPositionRef.current = { ...position };
    if (containerRef.current) {
      containerRef.current.style.cursor = "grabbing";
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const scaleFactor = 1 / zoom;
    const dx = (e.clientX - dragStartRef.current.x) * scaleFactor;
    const dy = (e.clientY - dragStartRef.current.y) * scaleFactor;

    setPosition({
      x: initialPositionRef.current.x + dx,
      y: initialPositionRef.current.y + dy,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (containerRef.current) {
      containerRef.current.style.cursor = "grab";
    }
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      if (containerRef.current) {
        containerRef.current.style.cursor = "grab";
      }
    }
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setIsDragging(true);
      dragStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };
      initialPositionRef.current = { ...position };

      // Disable transitions during dragging
      if (imgRef.current) {
        imgRef.current.style.transition = "none";
      }
    } else if (e.touches.length === 2) {
      setIsDragging(false);
      setInitialDistance(calculateDistance(e.touches));
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1 && isDragging) {
      e.preventDefault();
      const touch = e.touches[0];

      const dx = (touch.clientX - dragStartRef.current.x) / zoom;
      const dy = (touch.clientY - dragStartRef.current.y) / zoom;

      setPosition({
        x: initialPositionRef.current.x + dx,
        y: initialPositionRef.current.y + dy,
      });
    } else if (e.touches.length === 2) {
      e.preventDefault();
      const currentDistance = calculateDistance(e.touches);
      if (initialDistance && currentDistance) {
        const scale =
          1 + (currentDistance - initialDistance) * ZOOM_SENSITIVITY;
        const newZoom = Math.max(MIN_ZOOM, Math.min(zoom * scale, MAX_ZOOM));
        setZoom(newZoom);
        setInitialDistance(currentDistance);
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length === 0) {
      setIsDragging(false);
      setInitialDistance(null);

      // Re-enable transitions after dragging
      if (imgRef.current) {
        imgRef.current.style.transition =
          "transform 0.15s cubic-bezier(0.1, 0.7, 0.1, 1)";
      }
    } else if (e.touches.length === 1) {
      const touch = e.touches[0];
      setIsDragging(true);
      dragStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };
      initialPositionRef.current = { ...position };
      setInitialDistance(null);
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Mouse events
    container.addEventListener("wheel", handleWheel, { passive: false });
    container.addEventListener("mousemove", handleMouseMove);
    container.addEventListener("mouseup", handleMouseUp);
    container.addEventListener("mouseleave", handleMouseLeave);

    // Touch events
    container.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("touchend", handleTouchEnd);

    return () => {
      container.removeEventListener("wheel", handleWheel);
      container.removeEventListener("mousemove", handleMouseMove);
      container.removeEventListener("mouseup", handleMouseUp);
      container.removeEventListener("mouseleave", handleMouseLeave);
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [zoom, isDragging, initialDistance]);

  const preventImageDrag = (e) => {
    e.preventDefault();
  };

  return (
    <div
      className={`mapContainer ${dmMapUrl && "shrinkMapContainer"}`}
      onMouseDown={handleMouseDown}
      ref={containerRef}
      style={{ cursor: isDragging ? "grabbing" : "grab" }}
    >
      <img
        ref={imgRef}
        src={src}
        alt="Map"
        onDragStart={preventImageDrag}
        style={{
          transform: `scale(${zoom}) translate(${position.x}px, ${position.y}px)`,
          transformOrigin: "center center",
          willChange: "transform",
        }}
      />
    </div>
  );
};

export default Map;
